import React from "react";
import { useCountry } from "../contexts/CountryContext";

const S = ({ title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '10px' }}>{title}</h2>
    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>{children}</div>
  </section>
);

const CancellationPolicyPage = () => {
  const { country } = useCountry();
  return (
  <div className="page" data-testid="cancellation-policy-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
    <div className="page-header">
      <h1>Cancellation Policy</h1>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Last updated: April 2026</p>
    </div>

    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '32px' }}>
      <p style={{ fontSize: '0.9375rem', color: 'var(--text-dark)', lineHeight: 1.7, marginBottom: '28px' }}>
        This policy outlines the cancellation terms for services and subscriptions on HandyHub. Our goal is to provide a fair and transparent experience for both customers and providers.
      </p>

      <S title="1. Customer Cancellations">
        <p><strong>Quote Requests:</strong> Customers may cancel a quote request at any time before accepting a provider's quote. Cancelling a quote request is free and carries no obligation.</p>
        <p><strong>Accepted Jobs:</strong> If you have accepted a provider's quote, any cancellation should be communicated directly with the provider as soon as possible. Cancellation terms for accepted work are agreed between the customer and provider.</p>
        <p><strong>Communication:</strong> Customers are expected to provide timely notice of cancellations. Early communication helps providers manage their schedules effectively.</p>
      </S>

      <S title="2. Provider Cancellations">
        <p><strong>Declining Requests:</strong> Providers are not required to respond to every quote request and may decline requests that do not fit their availability, scope, or service area.</p>
        <p><strong>Accepted Jobs:</strong> If a provider cancels after a quote has been accepted, they should provide as much notice as possible. Repeated cancellations may negatively impact profile visibility or lead to account review.</p>
        <p><strong>Professional Standards:</strong> Providers are expected to honour commitments made through the platform. Consistent failure to do so may result in warnings, reduced visibility, or account suspension.</p>
      </S>

      <S title="3. No-Shows and Late Cancellations">
        <p>Both customers and providers are expected to act in good faith.</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Repeated no-shows or last-minute cancellations may impact account standing</li>
          <li>Providers may set their own cancellation expectations, which should be clearly communicated in advance</li>
          <li>Users are encouraged to resolve any issues directly and respectfully</li>
        </ul>
      </S>

      <S title="4. Subscription Cancellations">
        <p><strong>Monthly Subscriptions:</strong> You may cancel your monthly subscription at any time. Access will continue until the end of the current billing period. No partial refunds are provided for unused time.</p>
        <p><strong>Yearly Subscriptions:</strong> Annual subscriptions may be cancelled at any time. Access will continue until the end of the subscription period. Refunds, if any, are determined at HandyHub's discretion.</p>
        <p><strong>How to Cancel:</strong> Subscriptions can be managed via the provider dashboard under Subscription settings, or by contacting support.</p>
      </S>

      <S title="5. Timeframes">
        <p>While the platform does not enforce strict cancellation deadlines for quote requests, we recommend:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Cancelling unnecessary requests promptly</li>
          <li>Providing reasonable notice (e.g., at least 24 hours where possible) for accepted jobs</li>
          <li>Communicating directly with the other party for any urgent changes</li>
        </ul>
      </S>

      <S title="6. Disputes">
        <p>Any agreement for services is between the customer and the provider.</p>
        <p>HandyHub is not a party to service agreements and does not guarantee outcomes. In the event of a dispute, HandyHub may assist with communication but is not responsible for resolution, refunds, or service outcomes.</p>
      </S>

      <S title="7. Platform Role">
        <p>HandyHub acts as a marketplace connecting customers and providers. We do not provide services directly and are not responsible for cancellations, delays, or service performance.</p>
      </S>

      <S title="8. Contact">
        <p>For questions about cancellations, please contact: <a href={`mailto:${country.supportEmail}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }} data-testid="cancellation-support-email">{country.supportEmail}</a></p>
      </S>
    </div>
  </div>
  );
};

export default CancellationPolicyPage;
